/**
 * What each map layer is to a reader: its group in the layer panel, its name, the small sample
 * beside its switch, and the legend of the whole map. Every look is read from the style
 * definitions `buildLayers` draws with (layers.ts), so the panel and the legend cannot drift
 * from the map.
 */
import { CROWN_HEALTH, MAP, MATURITY, POSITION_SOURCE, SPECIES_COLORS, STRESS_STOPS } from '@/theme/colors';
import { POSITION_SOURCES } from './photoPositioning';
import {
  chmLegend,
  COLOR_UNITS,
  FIELD_TABLES,
  HEALTH_CATEGORIES,
  HEALTH_HOTSPOTS,
  isVisible,
  LINE_LOOKS,
  PHOTO_RANGES,
  POINT_LOOKS,
  rampColors,
  rampStops,
  rangeOf,
  TREE_MEASURES,
  UNIT_LEVEL_LOOKS,
  UNIT_LEVELS,
  UNIT_LOOK,
  UNIT_PARTS,
  type LayerOptions,
  type LayerRef,
  type LineLook,
  type ColorBy,
  type PhotoObjectRef,
  PHOTO_TILT_EXCLUDED_LOOK,
  photoColorByOf,
  tiltLimitOf,
  type PointLook,
  type TreeSchema,
  type UnitLevel,
} from './layers';
import { indexStops, isIndex } from './spectral';

/** The panel's groups, top first. */
export const LAYER_GROUPS = ['trees', 'areas', 'rasters'] as const;
export type LayerGroup = (typeof LAYER_GROUPS)[number];

/** What a layer shows, whatever object it is drawn from. */
export type LayerKey =
  | 'orthoCrowns'
  | 'healthCrowns'
  | 'healthHotspots'
  | 'mlsStems'
  | 'mlsCrowns'
  | 'fieldTrunks'
  | 'units'
  | 'unitsHit'
  | 'unitsOutline'
  | 'unitsDim'
  | 'mlsArea'
  | 'surveyArea'
  | 'scanAreas'
  | 'lhp'
  | 'boundary'
  | 'orthophoto'
  | 'dsm'
  | 'dtm'
  | 'chm'
  | 'chmQuality'
  | 'multispectral';

export function layerKey(ref: LayerRef): LayerKey {
  switch (ref.object_kind) {
    case 'ortho_tree_analysis':
      return 'orthoCrowns';
    case 'crown_health':
      return ref.table === HEALTH_HOTSPOTS ? 'healthHotspots' : 'healthCrowns';
    case 'mls_tree_analysis':
      return ref.table === 'crown' ? 'mlsCrowns' : 'mlsStems';
    case 'field_annotations':
      return ref.table === FIELD_TABLES.area ? 'surveyArea' : 'fieldTrunks';
    case 'forest_summary':
      if (ref.table === 'mls_area') return 'mlsArea';
      if (ref.table === 'field_area') return 'surveyArea';
      if (ref.table === UNIT_PARTS.hit) return 'unitsHit';
      if (ref.table === UNIT_PARTS.outline) return 'unitsOutline';
      if (ref.table === UNIT_PARTS.dim) return 'unitsDim';
      return 'units';
    case 'lidar_mls':
      return 'scanAreas';
    case 'lhp':
      return 'lhp';
    case 'dsm':
    case 'dtm':
    case 'chm':
    case 'orthophoto':
      return ref.object_kind;
    case 'chm_quality':
      return 'chmQuality';
    case 'multispectral_orthophoto':
      return 'multispectral';
    default:
      return 'boundary';
  }
}

const TREE_KEYS: LayerKey[] = ['orthoCrowns', 'healthCrowns', 'mlsStems', 'mlsCrowns', 'fieldTrunks'];

/** Which attribute table a tree layer's features follow. */
export function schemaOf(ref: LayerRef): TreeSchema {
  if (ref.object_kind === 'mls_tree_analysis') return 'mls';
  if (ref.object_kind === 'field_annotations') return 'field';
  return 'photo';
}
const RASTER_KEYS: LayerKey[] = ['orthophoto', 'dsm', 'dtm', 'chm', 'chmQuality', 'multispectral'];

export function groupOf(ref: LayerRef): LayerGroup {
  const key = layerKey(ref);
  if (TREE_KEYS.includes(key)) return 'trees';
  if (ref.via === 'tiler' || RASTER_KEYS.includes(key)) return 'rasters';
  return 'areas';
}

/** A layer that draws trees, which the genus filter thins. */
export const isTreeLayer = (ref: LayerRef) => ref.via === 'link' && TREE_KEYS.includes(layerKey(ref));
/** A crown health screen's crowns, which take its own colourings and the "only dead, snags and declining" switch. */
export const isHealthLayer = (ref: LayerRef) => ref.via === 'link' && layerKey(ref) === 'healthCrowns';
/** A layer of crowns or stems, which the crown style and the crown filters apply to; the colouring applies to every tree layer. */
export const isCrownLayer = (ref: LayerRef) => ref.via === 'link' && (ref.object_kind === 'ortho_tree_analysis' || ref.object_kind === 'mls_tree_analysis');

/** A small picture of a look: a line, a point marker, a crown, a fill or a colour ramp. */
export type Sample =
  | { kind: 'line'; look: LineLook }
  | { kind: 'point'; look: PointLook; color?: string }
  | { kind: 'crown'; colors: string[]; outline: string }
  | { kind: 'fill'; color: string }
  /** The whole swatch in one colour: a genus, a class of the colouring. */
  | { kind: 'swatch'; color: string }
  | { kind: 'ramp'; colors: string[] };

/** A few genus colours side by side: a tree layer is coloured by genus. */
const GENUS_SAMPLE = ['Picea', 'Fagus', 'Pinus'].map((g) => SPECIES_COLORS[g]!);

/** A few colours of the trees' colouring: genera, the ramp's low, middle and high, or the maturity classes. */
export function treeSampleColors(colorBy: ColorBy = 'species'): string[] {
  if (colorBy === 'species') return GENUS_SAMPLE;
  if (colorBy === 'maturity') return [MATURITY.mature, MATURITY.immature, MATURITY.mature];
  if (colorBy === 'health') return [CROWN_HEALTH.dead, CROWN_HEALTH.threatened, CROWN_HEALTH.healthy];
  if (colorBy === 'stress') return [STRESS_STOPS[0]!.color, STRESS_STOPS[4]!.color, STRESS_STOPS[STRESS_STOPS.length - 1]!.color];
  const ramp = rampColors();
  return [ramp[1]!, ramp[Math.floor(ramp.length / 2)]!, ramp[ramp.length - 1]!];
}

/** The sample beside a layer's switch, in the trees' current colouring; none for a picture (an orthophoto, a relief). */
export function sampleOf(ref: LayerRef, colorBy?: ColorBy): Sample | null {
  const colors = treeSampleColors(colorBy);
  switch (layerKey(ref)) {
    case 'orthoCrowns':
    case 'healthCrowns':
    case 'mlsCrowns':
      return { kind: 'crown', colors, outline: POINT_LOOKS.tree.stroke };
    case 'healthHotspots':
      return { kind: 'line', look: LINE_LOOKS.hotspot };
    case 'mlsStems':
      return { kind: 'point', look: POINT_LOOKS.tree, color: colors[0] };
    case 'fieldTrunks':
      return { kind: 'point', look: POINT_LOOKS.fieldTrunk, color: colors[1] };
    case 'units':
    case 'unitsHit':
    case 'unitsOutline':
      return { kind: 'line', look: UNIT_LOOK };
    case 'unitsDim':
      return { kind: 'fill', color: UNIT_LOOK.color };
    case 'mlsArea':
      return { kind: 'line', look: LINE_LOOKS.mlsArea };
    case 'surveyArea':
      return { kind: 'line', look: LINE_LOOKS.surveyArea };
    case 'scanAreas':
      return { kind: 'line', look: LINE_LOOKS.scanArea };
    case 'lhp':
      return { kind: 'line', look: LINE_LOOKS.lhp };
    case 'boundary':
      return { kind: 'line', look: LINE_LOOKS.boundary };
    case 'chm':
      return { kind: 'ramp', colors: rampColors() };
    default:
      return null;
  }
}

/** One line of the legend: what it says (a key in `ns`, `viewer` unless named) and what it looks like. */
export interface LegendEntry {
  id: string;
  label: string;
  ns?: 'viewer' | 'reports';
  sample: Sample;
  /** The unit level a unit outline stands for, named in the label. */
  level?: string;
  /** A ramp's ends, with the unit key of their values. */
  range?: { low: number; high: number; unitKey: string };
  /** A line under the label (a key in `viewer`). */
  hint?: string;
  /** Numbers the label interpolates, formatted for the locale (a list joined by slashes). */
  params?: Record<string, number | number[]>;
  /** The colour scale itself, the stops the map interpolates between: drawn as a bar with its values. */
  stops?: { value: number; color: string }[];
  /** How many features the entry stands for (a crown health category's crowns). */
  count?: number;
}

export interface LegendSection {
  id: 'genera' | 'colorBy' | 'trees' | 'boundaries' | 'rasters';
  entries: LegendEntry[];
  /** A line under the title (a key in `viewer`). */
  note?: string;
}

export interface LegendInput {
  refs: LayerRef[];
  options: LayerOptions;
  photos?: PhotoObjectRef[];
  /** The genera the page's tree layers hold; every genus of the palette when none are known. */
  genera?: string[];
  /** The unit levels the page's units hold; every level when none are known. */
  unitLevels?: string[];
  /** Band-1 value range per raster object (a visible CHM gets its ramp). */
  valueRanges?: Record<string, [number, number]>;
}

/**
 * The legend of a map: only what the page draws — the colouring of its tree layers (the genera
 * they hold, or the one scale of the quantity chosen), the markers of stems and trunks, the
 * outlines of units, scan and survey, and the ramps of its rasters.
 */
export function legendOf({ refs, options, photos = [], genera, unitLevels, valueRanges }: LegendInput): LegendSection[] {
  const drawn = refs.filter((r) => r.via !== 'none');
  const keys = new Set(drawn.map(layerKey));
  const sections: LegendSection[] = [];

  const by = options.colorBy ?? 'species';
  const treeLayers = drawn.filter(isTreeLayer);
  if (treeLayers.length && by === 'species') {
    const known = (genera ?? []).filter(Boolean);
    const list = known.length ? known : Object.keys(SPECIES_COLORS);
    sections.push({
      id: 'genera',
      entries: list.map((g) => ({ id: `genus-${g}`, label: `species.${g}`, ns: 'reports', sample: { kind: 'swatch', color: SPECIES_COLORS[g] ?? SPECIES_COLORS.Unknown! } })),
    });
  }

  if (treeLayers.length && by !== 'species') sections.push(colorBySection(by, options, treeLayers));
  if (drawn.some(isCrownLayer) && options.highlightMature) {
    const section = sections.find((s) => s.id === 'colorBy') ?? { id: 'colorBy' as const, entries: [] };
    if (!sections.includes(section)) sections.push(section);
    section.entries.push({ id: 'highlight', label: 'legend.highlightMature', sample: { kind: 'line', look: { color: MATURITY.highlight, width: 2.5 } } });
  }

  const trees: LegendEntry[] = [];
  const colors = treeSampleColors(by);
  if (keys.has('mlsStems')) trees.push({ id: 'mls-stem', label: 'legend.mlsStem', sample: { kind: 'point', look: POINT_LOOKS.tree, color: colors[0] } });
  if (keys.has('mlsStems')) trees.push({ id: 'mls-invalid', label: 'legend.mlsInvalid', sample: { kind: 'point', look: POINT_LOOKS.mlsInvalid } });
  if (keys.has('mlsCrowns')) trees.push({ id: 'mls-invalid-crown', label: 'legend.mlsInvalidCrown', sample: { kind: 'line', look: LINE_LOOKS.mlsInvalidCrown } });
  if (keys.has('fieldTrunks')) {
    trees.push({ id: 'field-trunk', label: 'legend.fieldTrunk', sample: { kind: 'point', look: POINT_LOOKS.fieldTrunk, color: colors[1] } });
    trees.push({ id: 'field-snag', label: 'legend.fieldSnag', sample: { kind: 'point', look: POINT_LOOKS.fieldSnag, color: colors[1] } });
    trees.push({ id: 'field-stump', label: 'legend.fieldStump', sample: { kind: 'point', look: POINT_LOOKS.fieldStump } });
  }
  if (trees.length) sections.push({ id: 'trees', entries: trees });

  const lines: LegendEntry[] = [];
  if (keys.has('units') || keys.has('unitsHit') || keys.has('unitsOutline')) {
    const known = (unitLevels ?? []).filter((l): l is UnitLevel => (UNIT_LEVELS as string[]).includes(l));
    const levels = known.length ? UNIT_LEVELS.filter((l) => known.includes(l)) : unitLevels?.length ? [] : UNIT_LEVELS;
    for (const level of levels) lines.push({ id: `unit-${level}`, label: 'legend.unitLevel', level, sample: { kind: 'line', look: UNIT_LEVEL_LOOKS[level] } });
    if (!levels.length) lines.push({ id: 'unit', label: 'legend.unit', sample: { kind: 'line', look: UNIT_LOOK } });
    if (options.selectedUnits !== undefined) lines.push({ id: 'unit-selected', label: 'legend.unitSelected', sample: { kind: 'line', look: LINE_LOOKS.unitSelected } });
  }
  if (keys.has('healthHotspots')) lines.push({ id: 'hotspot', label: 'legend.hotspot', sample: { kind: 'line', look: LINE_LOOKS.hotspot } });
  if (keys.has('mlsArea')) lines.push({ id: 'mls-area', label: 'legend.mlsArea', sample: { kind: 'line', look: LINE_LOOKS.mlsArea } });
  if (keys.has('surveyArea')) lines.push({ id: 'survey-area', label: 'legend.surveyArea', sample: { kind: 'line', look: LINE_LOOKS.surveyArea } });
  if (keys.has('scanAreas')) lines.push({ id: 'scan-area', label: 'legend.scanArea', sample: { kind: 'line', look: LINE_LOOKS.scanArea } });
  if (keys.has('lhp')) lines.push({ id: 'lhp', label: 'legend.lhp', sample: { kind: 'line', look: LINE_LOOKS.lhp } });
  if (keys.has('boundary')) lines.push({ id: 'boundary', label: 'legend.boundary', sample: { kind: 'line', look: LINE_LOOKS.boundary } });
  if (lines.length) sections.push({ id: 'boundaries', entries: lines });

  const rasters: LegendEntry[] = [];
  for (const ref of drawn) {
    const range = valueRanges?.[ref.object];
    if (ref.object_kind !== 'chm' || !range || !isVisible(options, ref.object, ref.object_kind)) continue;
    const { low, high, colors } = chmLegend(range);
    rasters.push({ id: `chm-${ref.object}`, label: 'layers.chmLegend', sample: { kind: 'ramp', colors }, range: { low, high, unitKey: 'units.m' } });
  }
  const spectral = options.spectral;
  if (spectral && isIndex(spectral.display) && spectral.stretch) {
    for (const ref of drawn) {
      if (ref.object_kind !== 'multispectral_orthophoto' || !isVisible(options, ref.object, ref.object_kind)) continue;
      rasters.push({ id: `index-${ref.object}`, label: `layers.indices.${spectral.display}`, sample: { kind: 'ramp', colors: indexStops(spectral.stretch).map((s) => s.color) }, stops: indexStops(spectral.stretch) });
    }
  }
  const photoBy = photoColorByOf(options, photos);
  if (photos.length && photoBy === 'position_source') rasters.push(...positionSourceEntries());
  if (photos.length && photoBy !== 'none' && photoBy !== 'position_source') {
    rasters.push({ id: `photos-${photoBy}`, label: `photoColorBy.${photoBy}`, sample: { kind: 'ramp', colors: rampColors() }, range: rangeEntry(PHOTO_RANGES[photoBy].range, PHOTO_RANGES[photoBy].unitKey) });
    if (photoBy === 'tilt_deg') {
      const limits = [...new Set(photos.map(tiltLimitOf))].sort((a, b) => a - b);
      rasters.push({ id: 'photos-tilt-excluded', label: 'legend.photoTiltExcluded', params: { limits }, sample: { kind: 'point', look: PHOTO_TILT_EXCLUDED_LOOK } });
    }
  }
  if (rasters.length) sections.push({ id: 'rasters', entries: rasters });
  return sections;
}

/** A photo point per source of its camera position, in the colours the map draws them with. */
export function positionSourceEntries(): LegendEntry[] {
  return POSITION_SOURCES.map((source) => ({
    id: `photos-source-${source}`,
    label: `legend.positionSource.${source}`,
    sample: { kind: 'point', look: { fill: POSITION_SOURCE[source], stroke: MAP.photoOutline, strokeWidth: 1 } },
  }));
}

/** The legend of the trees' colouring other than by genus: health categories, the stress ramp, maturity or a quantity's ramp. */
function colorBySection(by: Exclude<ColorBy, 'species'>, options: LayerOptions, treeLayers: LayerRef[]): LegendSection {
  if (by === 'health') {
    const counts = options.healthCounts;
    // Every category the screen knows; with the screen's counts, only those it has crowns in.
    const shown = HEALTH_CATEGORIES.filter((c) => !counts || (counts[c] ?? 0) > 0);
    const entries: LegendEntry[] = shown.map((c) => ({ id: `health-${c}`, label: `legend.health.${c}`, sample: { kind: 'swatch', color: CROWN_HEALTH[c] }, ...(counts ? { count: counts[c] ?? 0 } : {}) }));
    return { id: 'colorBy', entries, note: 'legend.healthNote' };
  }
  if (by === 'stress') {
    const entries: LegendEntry[] = [
      { id: 'stress', label: 'legend.stress', sample: { kind: 'ramp', colors: STRESS_STOPS.map((s) => s.color) }, stops: [...STRESS_STOPS], hint: 'legend.stressHint' },
      { id: 'no-value', label: 'legend.stressNone', sample: { kind: 'swatch', color: MAP.treeNoValue } },
    ];
    return { id: 'colorBy', entries };
  }
  // A layer that cannot carry the quantity at all (only field trunks: no volume) is named.
  const lacking = by === 'maturity' ? false : treeLayers.some((r) => TREE_MEASURES[schemaOf(r)][by] === null);
  const noValue: LegendEntry = { id: 'no-value', label: lacking ? 'legend.noValueField' : 'legend.noValue', sample: { kind: 'swatch', color: MAP.treeNoValue } };
  const entries: LegendEntry[] =
    by === 'maturity'
      ? [
          { id: 'mature', label: 'legend.mature', sample: { kind: 'swatch', color: MATURITY.mature } },
          { id: 'immature', label: 'legend.immature', sample: { kind: 'swatch', color: MATURITY.immature } },
          noValue,
        ]
      : [{ id: `ramp-${by}`, label: `legend.quantity.${by}`, sample: { kind: 'ramp', colors: rampColors() }, range: rangeEntry(rangeOf(options, by), COLOR_UNITS[by]), stops: rampStops(rangeOf(options, by)), ...(by === 'volume' ? { hint: 'legend.volumeSources' } : {}) }, noValue];
  return { id: 'colorBy', entries, note: 'legend.colorByAll' };
}

function rangeEntry([low, high]: readonly [number, number], unitKey: string) {
  return { low, high, unitKey };
}
